import { createSlice } from '@reduxjs/toolkit';

let initialState = {
  evenements: [],
  evenementSelect: '',
  participants: [],
  // Cache participants by event id (page 1 only, not persisted)
  participantsCache: {},
  participantSelect: '',
  // Participants navigation
  participantsNavigationIds: [],
  currentParticipantIndex: -1,
  participantsNavigationContext: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
  },
  prefetchedParticipants: {},
  programmeSelect: {},
  programmes: {},
  // Programmes navigation
  programmesNavigationIds: [],
  currentProgrammeIndex: -1,
  prefetchedProgrammes: {},
  maJournee: {},
  exposants: [],
  exposantsPremium: [],
  exposantSelect: {},
  // Exposants navigation
  exposantsNavigationIds: [],
  currentExposantIndex: -1,
  prefetchedExposants: {},
  planSelected: '',
  // List filters (persisted across navigation and pagination)
  participantsFilter: {
    catCheck: [],
    interestCheck: [],
    expertCheck: false,
    filtreMot: '',
    page: 1,
  },
  programmeFilter: { catCheck: [], themeCheck: [], filtreMot: '', page: 1 },
  exposantsFilter: { catCheck: [], filtreMot: '' },
};

const evenementSlice = createSlice({
  name: 'evenements',
  initialState,
  reducers: {
    addEvenements: (state, action) => {
      state.evenements = action.payload;
    },
    addEvenementSelect: (state, action) => {
      state.evenementSelect = action.payload;
    },
    addParticipants: (state, action) => {
      state.participants = action.payload;
    },
    setParticipantsCache: (state, action) => {
      const { eventId, data } = action.payload;
      if (!state.participantsCache) state.participantsCache = {};
      state.participantsCache[eventId] = data;
    },
    addParticipantSelect: (state, action) => {
      state.participantSelect = action.payload;
    },
    addProgrammeSelect: (state, action) => {
      state.programmeSelect = action.payload;
    },
    addProgramme: (state, action) => {
      state.programmes = action.payload;
    },
    addMajournee: (state, action) => {
      state.maJournee = action.payload;
    },
    removeProgrammeFromMaJournee: (state, action) => {
      state.maJournee = state.maJournee.filter(p => p.id !== action.payload);
    },
    addExposant: (state, action) => {
      state.exposants = action.payload;
    },
    addExposantPremium: (state, action) => {
      state.exposantsPremium = action.payload;
    },
    addExposantSelect: (state, action) => {
      state.exposantSelect = action.payload;
    },
    addPlan: (state, action) => {
      state.planSelected = action.payload;
    },
    // Participants navigation
    setParticipantsNavigation: (state, action) => {
      state.participantsNavigationIds = action.payload.ids;
      state.currentParticipantIndex = action.payload.currentIndex;
      if (action.payload.context) {
        state.participantsNavigationContext = action.payload.context;
      }
    },
    updateCurrentParticipantIndex: (state, action) => {
      state.currentParticipantIndex = action.payload;
    },
    clearParticipantsNavigation: state => {
      state.participantsNavigationIds = [];
      state.currentParticipantIndex = -1;
    },
    setPrefetchedParticipant: (state, action) => {
      state.prefetchedParticipants[action.payload.id] = action.payload.data;
    },
    clearPrefetchedParticipants: state => {
      state.prefetchedParticipants = {};
    },
    // Exposants navigation
    setExposantsNavigation: (state, action) => {
      state.exposantsNavigationIds = action.payload.ids;
      state.currentExposantIndex = action.payload.currentIndex;
    },
    updateCurrentExposantIndex: (state, action) => {
      state.currentExposantIndex = action.payload;
    },
    clearExposantsNavigation: state => {
      state.exposantsNavigationIds = [];
      state.currentExposantIndex = -1;
    },
    setPrefetchedExposant: (state, action) => {
      state.prefetchedExposants[action.payload.id] = action.payload.data;
    },
    clearPrefetchedExposants: state => {
      state.prefetchedExposants = {};
    },
    // Programmes navigation
    setProgrammesNavigation: (state, action) => {
      state.programmesNavigationIds = action.payload.ids;
      state.currentProgrammeIndex = action.payload.currentIndex;
    },
    updateCurrentProgrammeIndex: (state, action) => {
      state.currentProgrammeIndex = action.payload;
    },
    clearProgrammesNavigation: state => {
      state.programmesNavigationIds = [];
      state.currentProgrammeIndex = -1;
    },
    setPrefetchedProgramme: (state, action) => {
      state.prefetchedProgrammes[action.payload.id] = action.payload.data;
    },
    clearPrefetchedProgrammes: state => {
      state.prefetchedProgrammes = {};
    },
    setParticipantsFilter: (state, action) => {
      state.participantsFilter = action.payload;
    },
    setProgrammeFilter: (state, action) => {
      state.programmeFilter = action.payload;
    },
    setExposantsFilter: (state, action) => {
      state.exposantsFilter = action.payload;
    },
    resetEvenements: () => initialState,
  },
});

export const {
  addEvenements,
  addEvenementSelect,
  addParticipants,
  setParticipantsCache,
  addParticipantSelect,
  addProgramme,
  addProgrammeSelect,
  addMajournee,
  removeProgrammeFromMaJournee,
  addExposant,
  addExposantSelect,
  resetEvenements,
  addExposantPremium,
  addPlan,
  // Participants navigation
  setParticipantsNavigation,
  updateCurrentParticipantIndex,
  clearParticipantsNavigation,
  setPrefetchedParticipant,
  clearPrefetchedParticipants,
  // Exposants navigation
  setExposantsNavigation,
  updateCurrentExposantIndex,
  clearExposantsNavigation,
  setPrefetchedExposant,
  clearPrefetchedExposants,
  // Programmes navigation
  setProgrammesNavigation,
  updateCurrentProgrammeIndex,
  clearProgrammesNavigation,
  setPrefetchedProgramme,
  clearPrefetchedProgrammes,
  setParticipantsFilter,
  setProgrammeFilter,
  setExposantsFilter,
} = evenementSlice.actions;
export default evenementSlice.reducer;
